/**
 * Market Radar — operational validation surface.
 *
 * Purpose: open TSM and immediately judge whether the radar's machine view
 * agrees with what the market is actually doing. Dense and factual rather than
 * decorative; no trading controls, balances, positions, or private exchange
 * data exist anywhere on this page.
 */

import AppHeader from "@/components/AppHeader";
import { Helmet } from "react-helmet-async";
import React, { useMemo, useState } from "react";
import { Link } from "wouter";
import {
  useMarketRadarBoard,
  useMarketRadarMovers,
  type AttentionTier,
  type LifecycleState,
  type RadarSymbolRow,
} from "@/hooks/useMarketRadar";
import RadarSymbolDetail from "@/components/marketRadar/RadarSymbolDetail";
import RadarQuoteWindow from "@/components/marketRadar/RadarQuoteWindow";
import RadarAttentionDuration from "@/components/marketRadar/RadarAttentionDuration";
import {
  COLORS,
  DataQualityBadge,
  ExecutabilityCell,
  LifecycleBadge,
  MomentumCell,
  RadarStatusBadge,
  TierBadge,
  ageLabel,
  num,
  price,
  turnover,
} from "@/components/marketRadar/radarFormat";

type SortKey =
  | "rank" | "momentum" | "symbol" | "lastPrice" | "activityScore" | "rankingScore"
  | "turnover" | "volumeAcceleration" | "tradeRate" | "microVolatility"
  | "spreadBps" | "aggressorRatio" | "bookImbalance" | "updatedAt";

const LIFECYCLE_ORDER: LifecycleState[] = [
  "ACCELERATING", "ACTIVE", "PEAK", "EMERGING", "DECAYING", "DORMANT",
];

/**
 * Sort value for a row, with a null-safe ordering rule.
 *
 * Unmeasured values always sort to the BOTTOM regardless of direction, so a
 * SCAN symbol is never promoted to the top of a "highest trade rate" sort
 * simply because it has no trade feed — and never buried when sorting the
 * other way either.
 */
function sortValue(row: RadarSymbolRow, key: SortKey): number | string | null {
  switch (key) {
    case "rank": return row.rank;
    case "momentum":
      return row.rank !== null && row.previousRank !== null ? row.previousRank - row.rank : null;
    case "symbol": return row.symbol;
    case "lastPrice": return row.lastPrice;
    case "activityScore": return row.activityScore;
    case "rankingScore": return row.rankingScore;
    case "turnover": return row.turnover24hQuote;
    case "volumeAcceleration": return row.metrics.volumeAcceleration;
    case "tradeRate": return row.metrics.tradeRate;
    case "microVolatility": return row.metrics.microVolatility;
    case "spreadBps": return row.metrics.spreadBps;
    case "aggressorRatio": return row.metrics.aggressorRatio;
    case "bookImbalance": return row.metrics.bookImbalance;
    case "updatedAt": return Date.parse(row.updatedAt);
    default: return null;
  }
}

function compareRows(left: RadarSymbolRow, right: RadarSymbolRow, key: SortKey, ascending: boolean): number {
  const a = sortValue(left, key);
  const b = sortValue(right, key);
  // Unmeasured sinks, in both directions.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "string" || typeof b === "string") {
    const result = String(a).localeCompare(String(b));
    return ascending ? result : -result;
  }
  return ascending ? a - b : b - a;
}

function SummaryCell({ label, value, title }: { label: string; value: React.ReactNode; title?: string }) {
  return (
    <div title={title} style={{ minWidth: 76 }}>
      <div style={{ color: COLORS.fainter, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

const CELL: React.CSSProperties = {
  padding: "4px 6px",
  fontSize: 11,
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

export default function MarketRadar() {
  const board = useMarketRadarBoard();
  const movers = useMarketRadarMovers();

  const [sortKey, setSortKey] = useState<SortKey>("rank");
  const [ascending, setAscending] = useState(true);
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleState | "ALL">("ALL");
  const [tierFilter, setTierFilter] = useState<AttentionTier | "ALL">("ALL");
  const [minActivity, setMinActivity] = useState("");
  const [executableOnly, setExecutableOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [quoteWindowPrefill, setQuoteWindowPrefill] = useState<string | null>(null);
  const [quoteWindowPrefillRevision, setQuoteWindowPrefillRevision] = useState(0);

  const rows = board.data?.symbols ?? [];

  const filtered = useMemo(() => {
    const minimum = minActivity.trim() === "" ? null : Number(minActivity);
    const term = search.trim().toUpperCase();
    return rows.filter((row) => {
      if (lifecycleFilter !== "ALL" && row.lifecycleState !== lifecycleFilter) return false;
      if (tierFilter !== "ALL" && row.tier !== tierFilter) return false;
      if (executableOnly && row.executability.executable !== true) return false;
      if (minimum !== null && Number.isFinite(minimum)) {
        if (row.activityScore === null || row.activityScore < minimum) return false;
      }
      if (term && !row.symbol.includes(term)) return false;
      return true;
    });
  }, [rows, lifecycleFilter, tierFilter, executableOnly, minActivity, search]);

  const sorted = useMemo(
    () => [...filtered].sort((left, right) => compareRows(left, right, sortKey, ascending)),
    [filtered, sortKey, ascending],
  );

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setAscending((value) => !value);
      return;
    }
    setSortKey(key);
    // Rank reads best ascending (#1 first); every metric reads best descending.
    setAscending(key === "rank" || key === "symbol" || key === "spreadBps");
  }

  const header = (key: SortKey, label: string, title?: string) => (
    <th
      onClick={() => toggleSort(key)}
      title={title}
      style={{
        ...CELL,
        textAlign: "left",
        color: sortKey === key ? COLORS.accent : COLORS.dim,
        fontWeight: 700,
        fontSize: 9,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        cursor: "pointer",
        borderBottom: `1px solid ${COLORS.border}`,
        userSelect: "none",
        position: "sticky",
        top: 0,
        background: "#0a0a0a",
      }}
    >
      {label}{sortKey === key ? (ascending ? " ▲" : " ▼") : ""}
    </th>
  );

  const status = board.data?.status ?? (board.isLoading ? "warming" : "unavailable");
  const hottest = rows.find((row) => row.rank === 1) ?? null;

  return (
    <div style={{ background: "#0a0a0a", minHeight: "100vh" }}>
      <Helmet>
        <title>Market Radar — Tylee Smart Markets</title>
        <meta name="description" content="Read-only crypto market activity radar across the Bitget USDT futures universe." />
      </Helmet>
      <AppHeader />

      <main style={{ padding: "14px 16px 40px", maxWidth: 1600, margin: "0 auto" }}>
        <div className="radar-guide-affordance">
          <Link
            href="/radar/guide"
            className="radar-guide-link"
            aria-label="Read the Market Radar guide"
          >
            <span>RADAR GUIDE</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
        <h1 style={{ color: COLORS.text, fontSize: 15, fontWeight: 800, letterSpacing: "0.08em", margin: "0 0 4px" }}>
          MARKET RADAR
        </h1>
        <div style={{ color: COLORS.fainter, fontSize: 10, marginBottom: 12 }}>
          Read-only observation of the Bitget USDT futures universe. No trading, orders, balances or positions.
        </div>

        {/* Summary strip */}
        <div
          data-testid="radar-summary"
          style={{
            display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center",
            border: `1px solid ${COLORS.border}`, padding: "9px 12px", marginBottom: 12,
          }}
        >
          <RadarStatusBadge status={status} ageMs={board.data?.snapshotAgeMs ?? null} />
          <SummaryCell label="Universe" value={board.data?.universeSize ?? "—"} title="Eligible USDT futures contracts discovered" />
          <SummaryCell label="Ranked" value={board.data?.eligibleSize ?? "—"} title="Symbols passing the liquidity eligibility gate" />
          <SummaryCell label="Watch" value={board.data?.tiers.watch ?? "—"} />
          <SummaryCell label="Deep" value={board.data?.tiers.deep ?? "—"} />
          <SummaryCell
            label="Hottest"
            value={hottest ? <span style={{ color: COLORS.accent }}>{hottest.symbol}</span> : "—"}
            title="Current rank #1 by rankingScore"
          />
          <SummaryCell label="Rising" value={<span style={{ color: COLORS.good }}>{movers.data?.rising.length ?? "—"}</span>} />
          <SummaryCell label="Falling" value={<span style={{ color: "#c86464" }}>{movers.data?.falling.length ?? "—"}</span>} />
          <SummaryCell label="Snapshot age" value={ageLabel(board.data?.snapshotAgeMs ?? null)} />
        </div>

        {status === "warming" && (
          <div style={{ border: "1px solid #234", color: "#66aaff", fontSize: 11, padding: "7px 10px", marginBottom: 12 }}>
            The radar is still accumulating baseline history. Rankings are not yet reliable.
          </div>
        )}
        {status === "stale" && (
          <div style={{ border: `1px solid ${COLORS.warn}`, color: COLORS.warn, fontSize: 11, padding: "7px 10px", marginBottom: 12 }}>
            Not current market state. This board is served from the last stored snapshot
            {board.data?.snapshotAgeMs !== null && board.data?.snapshotAgeMs !== undefined
              ? ` (${ageLabel(board.data.snapshotAgeMs)} old)` : ""}.
          </div>
        )}
        {status === "unavailable" && (
          <div style={{ border: `1px solid ${COLORS.bad}`, color: COLORS.bad, fontSize: 11, padding: "7px 10px", marginBottom: 12 }}>
            No radar snapshot is available. The Market Radar worker may not be running.
          </div>
        )}

        <RadarQuoteWindow prefillSymbol={quoteWindowPrefill} prefillRevision={quoteWindowPrefillRevision} />

        <RadarAttentionDuration />

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <input
            aria-label="Search symbol"
            placeholder="SEARCH SYMBOL"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{
              background: "#111", border: "1px solid #2a2a2a", color: COLORS.text,
              fontSize: 11, padding: "4px 7px", width: 150, letterSpacing: "0.05em",
            }}
          />
          <select
            aria-label="Lifecycle filter"
            value={lifecycleFilter}
            onChange={(event) => setLifecycleFilter(event.target.value as LifecycleState | "ALL")}
            style={{ background: "#111", border: "1px solid #2a2a2a", color: COLORS.text, fontSize: 11, padding: "4px 7px" }}
          >
            <option value="ALL">ALL LIFECYCLE</option>
            {LIFECYCLE_ORDER.map((state) => <option key={state} value={state}>{state}</option>)}
          </select>
          <select
            aria-label="Tier filter"
            value={tierFilter}
            onChange={(event) => setTierFilter(event.target.value as AttentionTier | "ALL")}
            style={{ background: "#111", border: "1px solid #2a2a2a", color: COLORS.text, fontSize: 11, padding: "4px 7px" }}
          >
            <option value="ALL">ALL TIERS</option>
            <option value="deep">DEEP</option>
            <option value="watch">WATCH</option>
            <option value="scan">SCAN</option>
          </select>
          <input
            aria-label="Minimum activity score"
            placeholder="MIN ACTIVITY"
            value={minActivity}
            onChange={(event) => setMinActivity(event.target.value)}
            style={{ background: "#111", border: "1px solid #2a2a2a", color: COLORS.text, fontSize: 11, padding: "4px 7px", width: 110 }}
          />
          <label style={{ color: COLORS.dim, fontSize: 11, display: "flex", gap: 5, alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={executableOnly}
              onChange={(event) => setExecutableOnly(event.target.checked)}
            />
            EXECUTABLE ONLY
          </label>
          <span style={{ color: COLORS.fainter, fontSize: 10 }}>
            {sorted.length} of {rows.length} shown
          </span>
        </div>

        {selected && <RadarSymbolDetail symbol={selected} onClose={() => setSelected(null)} />}

        {/* Board */}
        <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}` }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1340 }}>
            <thead>
              <tr>
                {header("rank", "#")}
                {header("momentum", "Δ", "Rank movement since the previous evaluation")}
                {header("symbol", "Symbol")}
                {header("lastPrice", "Price", "Last traded price. Context only \u2014 never an input to the ranking")}
                {header("rankingScore", "Ranking", "Smoothed score that determines rank and tier")}
                {header("activityScore", "Activity", "Raw, unsmoothed activity measurement")}
                <th style={{ ...CELL, textAlign: "left", color: COLORS.dim, fontSize: 9, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, background: "#0a0a0a" }}>Lifecycle</th>
                <th style={{ ...CELL, textAlign: "left", color: COLORS.dim, fontSize: 9, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, background: "#0a0a0a" }}>Tier</th>
                {header("turnover", "24h Turnover")}
                {header("volumeAcceleration", "Vol Accel")}
                {header("tradeRate", "Trades/s")}
                {header("microVolatility", "μVol")}
                {header("spreadBps", "Spread bps")}
                {header("aggressorRatio", "Aggressor")}
                {header("bookImbalance", "Book Imb")}
                <th style={{ ...CELL, textAlign: "left", color: COLORS.dim, fontSize: 9, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, background: "#0a0a0a" }}>Exec</th>
                <th style={{ ...CELL, textAlign: "left", color: COLORS.dim, fontSize: 9, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, background: "#0a0a0a" }}>Data</th>
                {header("updatedAt", "Age")}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  // Keyed by symbol so React reuses the row across refreshes
                  // instead of tearing it down and rebuilding it.
                  key={row.symbol}
                  data-testid={`radar-row-${row.symbol}`}
                  onClick={() => setSelected(row.symbol === selected ? null : row.symbol)}
                  style={{
                    borderBottom: `1px solid ${COLORS.border}`,
                    cursor: "pointer",
                    background: row.symbol === selected ? "#151515" : "transparent",
                  }}
                >
                  <td style={{ ...CELL, color: COLORS.dim }}>{row.rank ?? "—"}</td>
                  <td style={CELL}><MomentumCell rank={row.rank} previousRank={row.previousRank} /></td>
                  <td style={{ ...CELL, color: COLORS.text, fontWeight: 700 }}>
                    <span>{row.symbol}</span>{" "}
                    <button
                      type="button"
                      aria-label={`Prefill retained quote window for ${row.symbol}`}
                      title="Fill the standalone retained quote form without reading it"
                      onClick={(event) => {
                        event.stopPropagation();
                        setQuoteWindowPrefill(row.symbol);
                        setQuoteWindowPrefillRevision((revision) => revision + 1);
                      }}
                      style={{ background: "transparent", border: "1px solid #333", color: COLORS.dim, cursor: "pointer", fontSize: 8, padding: "2px 4px" }}
                    >
                      QUOTES
                    </button>
                  </td>
                  <td style={{ ...CELL, color: COLORS.text }}>{price(row.lastPrice)}</td>
                  <td style={{ ...CELL, color: COLORS.accent }}>{num(row.rankingScore, 3)}</td>
                  <td style={{ ...CELL, color: "#66aaff" }}>{num(row.activityScore, 3)}</td>
                  <td style={CELL}><LifecycleBadge state={row.lifecycleState} /></td>
                  <td style={CELL}><TierBadge tier={row.tier} /></td>
                  <td style={{ ...CELL, color: COLORS.dim }}>{turnover(row.turnover24hQuote)}</td>
                  <td style={{ ...CELL, color: COLORS.text }}>{num(row.metrics.volumeAcceleration, 2, "x")}</td>
                  <td style={{ ...CELL, color: COLORS.text }}>{num(row.metrics.tradeRate, 2)}</td>
                  <td style={{ ...CELL, color: COLORS.text }}>{num(row.metrics.microVolatility, 4)}</td>
                  <td style={{ ...CELL, color: COLORS.text }}>{num(row.metrics.spreadBps, 2)}</td>
                  <td style={{ ...CELL, color: COLORS.text }}>{num(row.metrics.aggressorRatio, 2)}</td>
                  <td style={{ ...CELL, color: COLORS.text }}>{num(row.metrics.bookImbalance, 3)}</td>
                  <td style={CELL}>
                    <ExecutabilityCell
                      score={row.executability.score}
                      executable={row.executability.executable}
                      reasons={row.executability.reasons}
                    />
                  </td>
                  <td style={CELL}><DataQualityBadge quality={row.dataQuality} /></td>
                  <td style={{ ...CELL, color: COLORS.fainter }}>
                    {ageLabel(Date.now() - Date.parse(row.updatedAt))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sorted.length === 0 && (
            <div style={{ color: COLORS.fainter, fontSize: 11, padding: 16 }}>
              {rows.length === 0
                ? "No ranked symbols. The radar may still be warming up, or the worker may not be running."
                : "No symbols match the current filters."}
            </div>
          )}
        </div>

        <div style={{ color: COLORS.fainter, fontSize: 10, marginTop: 10, lineHeight: 1.6 }}>
          <strong style={{ color: COLORS.dim }}>Reading this board.</strong>{" "}
          <em>Ranking</em> is the smoothed score that decides rank and attention tier;{" "}
          <em>Activity</em> is the raw measurement behind it. <em>Price</em> is the last trade and is
          shown for orientation only — it is never an input to either score. <em>Exec</em> is liquidity
          only and is deliberately not folded into either score. A <span style={{ color: "#3a3a3a", fontStyle: "italic" }}>n/a</span>{" "}
          means the metric was not measured at that symbol's data quality — SCAN symbols are polled over
          REST and have no trade or order-book feed. That is an observation limit, not market inactivity.
        </div>
      </main>
    </div>
  );
}
